/**
 * Step 02 — Political Association & History
 *
 * Fields: politicalSpectrum, selfPartyAssociation, partyAssociationRole,
 * familyPartyAssociation, relativesInPolitics, hasContestedElection,
 * electionLevel, hasHeldPublicOffice, officeLevel,
 * networkAndCivicEngagement
 *
 * @module udp-schema/step-02
 */

import { z } from 'zod';
import { optionalTrimmedString } from './zod-helpers.js';

// ─── Main Step 02 Schema ────────────────────────────────────────────────────

export const step02PoliticalSchema = z.object({
  /** Political spectrum — left/right axis (matches dating-schema politicalOrientation values) */
  politicalSpectrum: z
    .enum([
      'far_left',
      'left',
      'centre_left',
      'centrist',
      'centre_right',
      'right',
      'far_right',
      'libertarian',
      'apolitical',
      'prefer_not_to_say',
    ])
    .optional(),

  /** Self party association — party key (e.g. "bjp", "inc", "none") */
  selfPartyAssociation: optionalTrimmedString,

  /** Nature of association with the party — only meaningful when selfPartyAssociation is a real party. */
  partyAssociationRole: z
    .enum([
      'member',
      'active_worker',
      'office_bearer',
      'candidate',
      'spokesperson',
      'volunteer',
      'sympathizer',
      'none',
      'prefer_not_to_say',
    ])
    .optional(),

  /** Family party association — same party-key enum via free string. */
  familyPartyAssociation: optionalTrimmedString,

  /** Relatives in politics (immediate / extended / both / none / prefer-not). */
  relativesInPolitics: z
    .enum(['none', 'immediate_family', 'extended_family', 'both', 'dont_know', 'prefer_not_to_say'])
    .optional(),

  // Public office + elections — split into 6 flat sub-steps replacing
  // the legacy single composite field. Each yes/no answer gets its
  // own screen; the level + year follow-ups are conditional on `yes`.
  // The 9-field nested-object shape (officeYears as range string,
  // electionResult, free-text details) is gone — those low-completion
  // fields cluttered the dense composite UI; the simpler shape matches
  // the rest of UDP onboarding.
  /** Has the respondent ever contested an election. */
  hasContestedElection: z.enum(['yes', 'no', 'prefer_not_to_say']).optional(),
  /** Level of the contested election. Only meaningful when
   *  `hasContestedElection === 'yes'`. */
  electionLevel: z
    .enum([
      'panchayat',
      'ward',
      'municipality',
      'block',
      'district',
      'assembly',
      'parliament',
      'other',
    ])
    .optional(),
  /** Year the respondent contested. Bounded 1900..2100 to keep the
   *  dataset sane against typos. Only meaningful when
   *  `hasContestedElection === 'yes'`. */
  electionYear: z.number().int().min(1900).max(2100).optional(),
  /** Has the respondent ever held a public office. */
  hasHeldPublicOffice: z.enum(['yes', 'no', 'prefer_not_to_say']).optional(),
  /** Level of the office held. Only meaningful when
   *  `hasHeldPublicOffice === 'yes'`. */
  officeLevel: z
    .enum([
      'panchayat',
      'ward',
      'municipality',
      'block',
      'district',
      'assembly',
      'parliament',
      'other',
    ])
    .optional(),
  /** Year the respondent held office. Same bounds rationale as
   *  `electionYear`. Only meaningful when `hasHeldPublicOffice ===
   *  'yes'`. */
  officeYear: z.number().int().min(1900).max(2100).optional(),

  /** Network and civic engagement (multi-select) */
  networkAndCivicEngagement: z.array(z.string().trim()).optional(),
});

// ─── Inferred types ─────────────────────────────────────────────────────────

export type Step02PoliticalInput = z.infer<typeof step02PoliticalSchema>;
